import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { ProductAttrValueSelect } from '../../../components/product/product-attr-value-select/product-attr-value-select';
import { ProductDescriptions } from '../../../components/product/product-descriptions/product-descriptions';
import { ProductImagesCarousel } from '../../../components/product/product-images-carousel/product-images-carousel';
import { ProductLocation } from '../../../components/product/product-location/product-location';
import { ProductRecommendedList } from '../../../components/product/product-recommended-list/product-recommended-list';
import { ProductSizeTableButton } from '../../../components/product/product-size-table-button/product-size-table-button';
import { UnlockProductPriceCard } from '../../../components/product/unlock-product-price-card/unlock-product-price-card';
import { AddToCartButton } from '../../../components/shopping/add-to-cart-button/add-to-cart-button';
import { Product, ProductAttrValue } from '../../../models/product';
import { FileApiService } from '../../../services/apis/file-api.service';
import { ProductApiService } from '../../../services/apis/product-api.service';
import { AuthService } from '../../../services/auth.service';
import { TitleService } from '../../../services/title.service';
import { mergeRouter } from '../../../utils/router';

interface ProductTagDetail {
  value: string;
  label: string;
  description: string;
  icon: string;
  keywords: string[];
}

@Component({
  selector: 'app-product-page',
  imports: [
    ProductImagesCarousel,
    MatIconModule,
    RouterModule,
    ProductLocation,
    DecimalPipe,
    ProductAttrValueSelect,
    MatButtonModule,
    ProductDescriptions,
    ProductSizeTableButton,
    ProductRecommendedList,
    AddToCartButton,
    MatTooltipModule,
    UnlockProductPriceCard,
  ],
  templateUrl: './product-page.html',
  styleUrl: './product-page.scss',
})
export class ProductPage implements OnInit {
  private readonly productTagDetails: Record<string, ProductTagDetail> = {
    Train: {
      value: 'Train',
      label: 'Train',
      description: 'Supportive activewear for lifting, circuits, and high-energy studio days.',
      icon: 'fitness_center',
      keywords: ['training activewear', 'workout clothes', 'gym clothes'],
    },
    Stretch: {
      value: 'Stretch',
      label: 'Stretch',
      description: 'Soft stretch pieces for yoga, barre, and slower movement rituals.',
      icon: 'self_improvement',
      keywords: ['yoga clothes', 'stretch activewear', 'barre clothes'],
    },
    Layer: {
      value: 'Layer',
      label: 'Layer',
      description: 'Easy layers for warmups, cool-downs, and everyday athleisure styling.',
      icon: 'layers',
      keywords: ['activewear layers', 'athleisure', 'everyday activewear'],
    },
  };

  loading: boolean = false;
  product?: Product;
  attrs: ProductAttrValue[] = [];
  pricedAttrValue?: ProductAttrValue;
  constructor(
    private route: ActivatedRoute,
    private title: TitleService,
    private productApi: ProductApiService,
    private fileApi: FileApiService,
    private router: Router,
    private auth: AuthService
  ) {
    this.title.setTitle('');
  }

  get free_shipping_threshold() {
    return environment.free_shipping_threshold;
  }

  get is_authenticated() {
    return this.auth.profile() != null;
  }

  get visibleTagDetails() {
    return this.product ? this.getProductTagDetails(this.product) : [];
  }

  queryAttrValueIDs: number[] = [];
  queryPricedAttrValueID: number = 0;

  get deliveryDate() {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    const from = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    date.setDate(date.getDate() + 3);
    const to = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    return `${from} - ${to}`;
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      const no = params['no'] ?? '';
      this.getProduct(no);
    });
    this.route.queryParams.subscribe((params) => {
      const attr_value_ids = params['attr_ids'];
      if (attr_value_ids instanceof Array) {
        this.queryAttrValueIDs = attr_value_ids.map((v: string) => parseInt(v));
      } else if (attr_value_ids) {
        this.queryAttrValueIDs = [parseInt(attr_value_ids)];
      }
      this.queryPricedAttrValueID = parseInt(params['priced_attr_id'] ?? '0');
    });
  }

  async getProduct(no: string) {
    try {
      this.loading = true;
      this.product = undefined;
      this.attrs = [];
      this.pricedAttrValue = undefined;
      this.product = await this.productApi.getProduct(no);
      this.setProductSeo(this.product);

      this.attrs = [];
      outloop: for (const attr of this.product.attrs) {
        for (const value of attr.values) {
          if (this.queryAttrValueIDs.includes(value.id)) {
            this.attrs.push(value);
            continue outloop;
          }
        }
        this.attrs.push(attr.values[0]);
      }
      this.pricedAttrValue = this.product.priced_attr.values[0];

      for (const value of this.product.priced_attr.values) {
        if (value.id === this.queryPricedAttrValueID) {
          this.pricedAttrValue = value;
          break;
        }
      }
    } catch (error) {
    } finally {
      this.loading = false;
    }
  }

  private setProductSeo(product: Product) {
    const description = this.buildProductDescription(product);
    const url = `/product/${product.no}`;
    const tagDetails = this.getProductTagDetails(product);
    const tagLabels = tagDetails.map((tag) => tag.label);
    const tagKeywords = tagDetails.flatMap((tag) => tag.keywords);
    const pageTitle = tagLabels.length
      ? `${product.name} | ${tagLabels.join(' & ')} Activewear`
      : product.name;
    const image = product.images[0]
      ? this.fileApi.fileurl(product.images[0])
      : '/images/logo512.webp';
    const productImages = product.images.length
      ? product.images.map((item) => this.fileApi.fileurl(item))
      : [this.title.absoluteUrl('/images/logo512.webp')];
    const categories = product.categories
      ?.map((category) => category.name)
      .filter(Boolean)
      .join(' > ');

    this.title.setPageMeta({
      title: pageTitle,
      canonicalPath: url,
      description,
      image,
      imageAlt: `${product.name} by ${environment.appname}`,
      type: 'product',
      keywords: [
        product.name,
        "women's activewear",
        'yoga clothes',
        'workout clothes',
        'sports bras',
        'leggings',
        ...tagLabels.map((tag) => `${tag.toLowerCase()} activewear`),
        ...tagKeywords,
        ...(product.fabric ? [product.fabric] : []),
        ...(product.categories?.map((category) => category.name) ?? []),
      ],
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description,
          image: productImages,
          sku: product.no,
          keywords: [
            product.name,
            ...tagLabels,
            ...tagKeywords,
            ...(product.categories?.map((category) => category.name) ?? []),
          ].join(', '),
          brand: {
            '@type': 'Brand',
            name: environment.appname,
          },
          category: categories,
          material: product.fabric || undefined,
          url: this.title.absoluteUrl(url),
          additionalProperty: this.productProperties(product),
          offers: this.productOffers(product, url),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: environment.host,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: product.name,
              item: this.title.absoluteUrl(url),
            },
          ],
        },
      ],
    });
  }

  private buildProductDescription(product: Product) {
    const fallback = `Shop ${product.name} from ${environment.appname}, featuring supportive activewear details, comfortable styling, and easy everyday wear.`;
    const description = product.descriptions.find((item) => item.trim()) ?? fallback;
    const tagDetails = this.getProductTagDetails(product);
    const tagPhrase = tagDetails.length
      ? ` Best for ${tagDetails.map((tag) => tag.label.toLowerCase()).join(', ')}.`
      : '';
    return this.truncate(`${this.stripHtml(description)}${tagPhrase}`, 160);
  }

  private productProperties(product: Product) {
    const tagDetails = this.getProductTagDetails(product);

    return [
      ...(tagDetails.length
        ? [
          {
            '@type': 'PropertyValue',
            name: 'Best for',
            value: tagDetails.map((tag) => tag.label).join(', '),
          },
        ]
        : []),
      ...(product.fabric
        ? [
          {
            '@type': 'PropertyValue',
            name: 'Fabric',
            value: product.fabric,
          },
        ]
        : []),
      ...product.attrs.map((attr) => ({
        '@type': 'PropertyValue',
        name: attr.name,
        value: attr.values.map((value) => value.name).join(', '),
      })),
      {
        '@type': 'PropertyValue',
        name: product.priced_attr.name,
        value: product.priced_attr.values.map((value) => value.name).join(', '),
      },
    ];
  }

  private getProductTagDetails(product: Product) {
    const seen = new Set<string>();

    return (product.tags ?? []).reduce<ProductTagDetail[]>((details, tag) => {
      const detail = this.productTagDetails[tag];

      if (!detail || seen.has(detail.value)) {
        return details;
      }

      seen.add(detail.value);
      details.push(detail);
      return details;
    }, []);
  }

  private productOffers(product: Product, productPath: string) {
    const offers = product.priced_attr.values
      .filter((value) => this.isValidPrice(value.price))
      .map((value) => ({
        '@type': 'Offer',
        url: this.productOfferUrl(productPath, value.id),
        sku: `${product.no}-${value.id}`,
        price: this.formatPrice(value.price),
        priceCurrency: 'USD',
        availability: this.productAvailability(product),
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: environment.appname,
        },
      }));

    return offers.length ? offers : undefined;
  }

  private productOfferUrl(productPath: string, pricedAttrValueId: number) {
    const params = new URLSearchParams({
      priced_attr_id: String(pricedAttrValueId),
    });
    return `${environment.host}${productPath}?${params.toString()}`;
  }

  private productAvailability(product: Product) {
    const status = product.status.toLowerCase();

    if (status.includes('discontinued')) {
      return 'https://schema.org/Discontinued';
    }

    if (
      status.includes('out') ||
      status.includes('sold') ||
      status.includes('unavailable')
    ) {
      return 'https://schema.org/OutOfStock';
    }

    return 'https://schema.org/InStock';
  }

  private isValidPrice(price: number) {
    return Number.isFinite(price) && price >= 0;
  }

  private formatPrice(price: number) {
    return price.toFixed(2);
  }

  private stripHtml(value: string) {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  private truncate(value: string, maxLength: number) {
    if (value.length <= maxLength) {
      return value;
    }

    return `${value.slice(0, maxLength - 3).trim()}...`;
  }

  selectAttr(index: number, value: ProductAttrValue) {
    this.attrs[index] = value;
    mergeRouter(this.router, this.route, {
      attr_ids: this.attrs.map((v) => v.id),
      priced_attr_id: this.pricedAttrValue?.id,
    });
  }
  selectPricedAttr(value: ProductAttrValue) {
    this.pricedAttrValue = value;
    mergeRouter(this.router, this.route, {
      attr_ids: this.attrs.map((v) => v.id),
      priced_attr_id: this.pricedAttrValue?.id,
    });
  }
}
